import { useEffect, useRef, useState } from "react";
import { Button, Card } from "@omnichannel/ui";
import { ApiError, request } from "./api";
import { money } from "./money";
import { RefundCase } from "./RefundCase";

interface Hold {
  id: string;
  intent_id: string;
  status: string;
  created_at: string;
}
interface Observation {
  id: string;
  intent_id: string;
  provider: string;
  source_kind: string;
  source_id: string;
  amount_minor: number;
  cumulative_refunded_minor: number;
  currency: string;
  occurred_at: string;
}
interface Reconciliation {
  holds: Hold[];
  observations: Observation[];
}

const date = (value: string) => new Date(value).toLocaleString("ru-RU");

export function RefundReconciliation({ personId }: { personId: string }) {
  const [data, setData] = useState<Reconciliation | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    generation.current++;
    setData(null);
    setPending(false);
    setError("");
    setSelected(null);
    return () => { generation.current++; };
  }, [personId]);

  const load = async () => {
    const current = ++generation.current;
    setPending(true);
    setError("");
    try {
      const next = await request<Reconciliation>(
        `/api/v1/persons/${personId}/wallet/refund-reconciliation`,
      );
      if (current === generation.current) setData(next);
    } catch (failure) {
      if (current === generation.current) {
        setError(failure instanceof ApiError ? failure.message : "Не удалось загрузить сведения о возвратах.");
      }
    } finally {
      if (current === generation.current) setPending(false);
    }
  };

  const active = data?.holds.filter((hold) => hold.status === "active") || [];
  return (
    <Card className="person-section">
      <section aria-label="Возвраты ЮKassa и сверка">
        <h2>Возвраты ЮKassa и сверка</h2>
        <Button variant="secondary" disabled={pending} onClick={() => void load()}>
          {data ? "Обновить сведения о возвратах" : "Проверить возвраты ЮKassa"}
        </Button>
        {pending && <p role="status">Загрузка сведений о возвратах…</p>}
        {error && <p role="alert">{error}{data && " Показанные сведения могут быть устаревшими."}</p>}
        {data && (
          <>
            {active.length > 0 ? (
              <p role="status">Новые AI-задачи заблокированы: требуется ручная сверка возврата пополнения. Показано активных блокировок: {active.length}.</p>
            ) : (
              <p>Активных блокировок из-за возвратов в полученных данных нет.</p>
            )}
            <p>Сверьте платёж и возврат в ЮKassa с проводками кошелька. Эта карточка показывает записи сверки; баланс и блокировки здесь не изменяются.</p>
            <h3>Блокировки</h3>
            {data.holds.length ? (
              <div className="table-scroll"><table>
                <thead><tr><th>Дата</th><th>Платёж в хабе</th><th>Статус</th><th>Сверка</th></tr></thead>
                <tbody>{data.holds.map((hold) => (
                  <tr key={hold.id}><td>{date(hold.created_at)}</td><td>{hold.intent_id}</td><td>{hold.status === "active" ? "Требуется сверка" : hold.status}</td><td><Button variant="ghost" aria-label={`Открыть платёж ${hold.intent_id}`} onClick={() => setSelected(hold.intent_id)}>Сведения о платеже</Button></td></tr>
                ))}</tbody>
              </table></div>
            ) : <p>Записей блокировки нет.</p>}
            <h3>Подтверждённые сведения провайдера</h3>
            <p>Отдельный возврат и снимок общей суммы могут описывать один возврат. Не складывайте эти записи.</p>
            {data.observations.length ? (
              <div className="table-scroll"><table>
                <thead><tr><th>Дата</th><th>Платёж в хабе</th><th>Источник</th><th>ID провайдера</th><th>Сумма записи</th><th>Общая сумма на момент записи</th></tr></thead>
                <tbody>{data.observations.map((observation) => (
                  <tr key={observation.id}>
                    <td>{date(observation.occurred_at)}</td><td>{observation.intent_id}</td>
                    <td>{observation.provider} · {observation.source_kind === "refund" ? "Отдельный возврат" : observation.source_kind === "payment_snapshot" ? "Снимок платежа" : observation.source_kind}</td>
                    <td>{observation.source_id}</td><td>{money(observation.amount_minor, observation.currency)}</td>
                    <td>{money(observation.cumulative_refunded_minor, observation.currency)}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            ) : <p>Подтверждённых возвратов нет.</p>}
            <p>Показано до 200 последних записей каждого типа.</p>
            {selected && <RefundCase key={`${personId}:${selected}`} personId={personId} intentId={selected} />}
          </>
        )}
      </section>
    </Card>
  );
}
